import React, { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { AlertTriangle, BookOpen, CheckCircle2, History, Loader2, PanelLeftClose, PanelLeftOpen, Plus, RefreshCw, RotateCcw, Save, Send, Upload, Video, VideoOff } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  getContentHistory,
  listContentItems,
  openContentDraft,
  publishContent,
  restoreContentVersion,
  retireContent,
  saveContentDraft,
  submitContentReview,
  validateContent,
  type ContentDraftView,
  type ContentHistoryView,
  type ContentItemSummary,
  type ContentTypeRef,
  type ContentValidationReport,
} from './adminContentApi';

type ContentStudioPlugin<TDocument> = {
  type: ContentTypeRef;
  title: string;
  description: string;
  createEmptyDocument: (slug: string) => TDocument;
  getSlug: (document: TDocument) => string;
  renderEditor: (document: TDocument, onChange: (next: TDocument) => void) => ReactNode;
  renderPreview: (document: TDocument) => ReactNode;
  renderFilters?: (
    filter: Record<string, string | undefined>,
    onChange: (patch: Record<string, string | undefined>) => void,
  ) => ReactNode;
  renderSummaryMeta?: (item: ContentItemSummary) => ReactNode;
  renderSummaryMedia?: (item: ContentItemSummary) => ReactNode;
};

export function CatalogMediaThumbnail({
  previewPath,
  attachedLabel = '영상 있음',
  missingLabel = '영상 없음',
}: {
  previewPath: string | null;
  attachedLabel?: string;
  missingLabel?: string;
}) {
  const label = previewPath ? attachedLabel : missingLabel;
  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-md border bg-muted">
      {previewPath ? (
        <img
          alt={label}
          className="h-full w-full bg-black object-cover"
          decoding="async"
          loading="lazy"
          src={previewPath}
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center"><VideoOff className="h-5 w-5 text-muted-foreground" /></div>
      )}
      <span className="absolute inset-x-0 bottom-0 flex items-center gap-1 bg-black/70 px-1.5 py-1 text-[10px] font-medium leading-none text-white">
        {previewPath ? <Video className="h-3 w-3" /> : <VideoOff className="h-3 w-3" />}{label}
      </span>
    </div>
  );
}

const errorMessage = (error: unknown): string =>
  error instanceof Error && error.message.trim() ? error.message : '콘텐츠 요청을 처리하지 못했습니다.';

function StatusBadge({ item }: { item: ContentItemSummary }) {
  if (item.validation.errorCount > 0) return <Badge variant="destructive">오류 {item.validation.errorCount}</Badge>;
  if (item.draftStatus === 'IN_REVIEW') return <Badge variant="secondary">검토 대기</Badge>;
  if (item.publicationStatus === 'PUBLISHED') return <Badge variant="outline">발행 v{item.currentVersionNumber}</Badge>;
  if (item.publicationStatus === 'RETIRED') return <Badge variant="destructive">폐기</Badge>;
  return <Badge variant="secondary">초안</Badge>;
}

export default function ContentStudioShell<TDocument>({
  plugin,
  titleAs = 'h1',
}: {
  plugin: ContentStudioPlugin<TDocument>;
  titleAs?: 'h1' | 'h2';
}) {
  const [items, setItems] = useState<ContentItemSummary[]>([]);
  const [filter, setFilter] = useState<Record<string, string | undefined>>({});
  const [draft, setDraft] = useState<ContentDraftView<TDocument> | null>(null);
  const [document, setDocument] = useState<TDocument | null>(null);
  const [validation, setValidation] = useState<ContentValidationReport | null>(null);
  const [history, setHistory] = useState<ContentHistoryView | null>(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [catalogVisible, setCatalogVisible] = useState(true);

  const loadCatalog = useCallback(async () => {
    setLoading(true);
    setError(null);
    try { setItems(await listContentItems(plugin.type, filter)); }
    catch (failure) { setError(errorMessage(failure)); }
    finally { setLoading(false); }
  }, [filter, plugin.type.applicationKey, plugin.type.contentTypeKey]);

  useEffect(() => { void loadCatalog(); }, [loadCatalog]);

  const loadHistory = useCallback(async (slug: string) => {
    setHistory(await getContentHistory(plugin.type, slug));
  }, [plugin.type.applicationKey, plugin.type.contentTypeKey]);

  const selectItem = async (slug: string) => {
    if (dirty && !window.confirm('저장하지 않은 변경을 버리고 다른 콘텐츠를 열까요?')) return;
    setWorking(true); setError(null);
    try {
      const [opened, itemHistory] = await Promise.all([
        openContentDraft<TDocument>(plugin.type, slug),
        getContentHistory(plugin.type, slug),
      ]);
      setDraft(opened); setDocument(opened.document); setValidation(opened.validation);
      setHistory(itemHistory); setDirty(false); setCatalogVisible(false);
    } catch (failure) { setError(errorMessage(failure)); }
    finally { setWorking(false); }
  };

  const startNew = () => {
    if (dirty && !window.confirm('저장하지 않은 변경을 버리고 새 콘텐츠를 만들까요?')) return;
    const slug = window.prompt('새 콘텐츠 slug를 입력하세요.');
    if (slug === null) return;
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) {
      setError('slug는 소문자·숫자·하이픈만 사용할 수 있습니다.');
      return;
    }
    setDraft(null); setDocument(plugin.createEmptyDocument(slug)); setValidation(null);
    setHistory(null); setDirty(true); setError(null); setCatalogVisible(false);
  };

  const runDraftAction = async (action: () => Promise<ContentDraftView<TDocument>>) => {
    setWorking(true); setError(null);
    try {
      const next = await action();
      setDraft(next); setDocument(next.document); setValidation(next.validation); setDirty(false);
      await Promise.all([loadCatalog(), loadHistory(next.key.slug)]);
    } catch (failure) { setError(errorMessage(failure)); }
    finally { setWorking(false); }
  };

  const issues = useMemo(() => validation?.issues ?? [], [validation]);
  const selectedSlug = document ? plugin.getSlug(document) : null;
  const Title = titleAs;

  return (
    <div className="w-full min-w-0 space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><Title className="text-2xl font-bold">{plugin.title}</Title><p className="mt-1 text-sm text-muted-foreground">{plugin.description}</p></div>
        <div className="flex gap-2">
          <Button onClick={startNew}><Plus className="mr-2 h-4 w-4" />새 콘텐츠</Button>
          <Button variant="outline" onClick={() => void loadCatalog()} disabled={loading}><RefreshCw className="mr-2 h-4 w-4" />새로고침</Button>
        </div>
      </div>
      {error && <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">{error}</div>}

      <div className={`grid min-w-0 gap-5 ${catalogVisible ? 'xl:grid-cols-[18rem_minmax(0,1fr)]' : 'grid-cols-1'}`}>
        {catalogVisible && <aside className="min-w-0 space-y-3 xl:sticky xl:top-4 xl:self-start">
          <Card><CardContent className="grid gap-2 p-4">
            <Input placeholder="slug 검색" value={filter.query ?? ''} onChange={(event) => setFilter((current) => ({ ...current, query: event.target.value || undefined }))} />
            {plugin.renderFilters?.(filter, (patch) => setFilter((current) => ({ ...current, ...patch })))}
          </CardContent></Card>
          <div className="grid max-h-[70vh] gap-2 overflow-y-auto pr-1">
            {loading ? <div className="flex justify-center p-8"><Loader2 className="h-5 w-5 animate-spin" /></div> : items.map((item) => (
              <button key={item.key.slug} type="button" onClick={() => void selectItem(item.key.slug)} className={`min-w-0 rounded-lg border p-3 text-left transition hover:bg-muted ${selectedSlug === item.key.slug ? 'border-primary bg-primary/5' : ''}`}>
                <div className={plugin.renderSummaryMedia ? 'grid min-w-0 grid-cols-[6.5rem_minmax(0,1fr)] gap-3' : 'min-w-0'}>
                  {plugin.renderSummaryMedia?.(item)}
                  <div className="min-w-0">
                    <div className="flex min-w-0 items-start justify-between gap-2"><span className="truncate text-sm font-semibold">{item.title}</span><StatusBadge item={item} /></div>
                    <div className="mt-2 text-xs text-muted-foreground">{plugin.renderSummaryMeta?.(item)}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </aside>}

        <main className="min-w-0">
          {!document ? (
            <Card><CardContent className="flex min-h-80 flex-col items-center justify-center text-center"><BookOpen className="h-10 w-10 text-muted-foreground" /><p className="mt-4 font-semibold">편집할 콘텐츠를 선택하세요</p><p className="mt-1 text-sm text-muted-foreground">기존 발행 버전에서 초안을 열거나 새 콘텐츠를 만드세요.</p></CardContent></Card>
          ) : (
            <div className="grid min-w-0 gap-5">
              <div className="sticky top-0 z-20 grid min-w-0 gap-2 rounded-xl border bg-background/95 p-3 shadow-sm backdrop-blur">
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => setCatalogVisible((visible) => !visible)} aria-label={catalogVisible ? '콘텐츠 목록 숨기기' : '콘텐츠 목록 보기'}>
                    {catalogVisible ? <PanelLeftClose className="mr-2 h-4 w-4" /> : <PanelLeftOpen className="mr-2 h-4 w-4" />}{catalogVisible ? '목록 숨기기' : '목록 보기'}
                  </Button>
                  <div className="mr-auto min-w-0"><p className="truncate font-semibold">{selectedSlug}</p><p className="text-xs text-muted-foreground">{draft ? `revision ${draft.revision} · ${draft.status}` : '새 초안'}{dirty ? ' · 저장 안 됨' : ''}</p></div>
                  <Button size="sm" variant="outline" disabled={working} onClick={() => void (async () => { setWorking(true); setError(null); try { setValidation(await validateContent(plugin.type, document)); } catch (failure) { setError(errorMessage(failure)); } finally { setWorking(false); } })()}><CheckCircle2 className="mr-2 h-4 w-4" />검증</Button>
                  <Button size="sm" disabled={working || !dirty} onClick={() => void runDraftAction(() => saveContentDraft(plugin.type, plugin.getSlug(document), draft?.revision ?? null, document))}><Save className="mr-2 h-4 w-4" />저장</Button>
                  <Button size="sm" variant="secondary" disabled={working || dirty || !draft || !validation?.publishable || draft.status !== 'DRAFT'} onClick={() => draft && void runDraftAction(() => submitContentReview<TDocument>(plugin.type, draft.key.slug, draft.revision))}><Send className="mr-2 h-4 w-4" />검토 요청</Button>
                  <Button size="sm" disabled={working || !draft || draft.status !== 'IN_REVIEW'} onClick={() => { if (!draft) return; const confirmation = window.prompt(`발행하려면 ${draft.key.slug} 를 입력하세요.`); if (confirmation !== null) void runDraftAction(() => publishContent<TDocument>(plugin.type, draft.key.slug, draft.revision, confirmation)); }}><Upload className="mr-2 h-4 w-4" />발행</Button>
                </div>
                <nav aria-label="상세 바로가기" className="flex min-w-0 gap-1 overflow-x-auto border-t pt-2 text-xs">
                  {[
                    ['#cms-media', '영상'],
                    ['#cms-narrative', '서사'],
                    ['#cms-basics', '기본 정보'],
                    ['#cms-listening', '1 듣기'],
                    ['#cms-expressions', '2 표현'],
                    ['#cms-reading', '3 읽기·쓰기'],
                    ['#cms-preview', '미리보기'],
                  ].map(([href, label]) => <a key={href} href={href} className="inline-flex h-8 shrink-0 items-center rounded-md px-3 font-medium hover:bg-muted">{label}</a>)}
                </nav>
              </div>

              <div className="grid min-w-0 gap-5 xl:grid-cols-[minmax(0,1fr)_22rem]">
                <div className="grid min-w-0 gap-5">{plugin.renderEditor(document, (next) => { setDocument(next); setDirty(true); })}</div>
                <aside className="grid min-w-0 content-start gap-5 xl:sticky xl:top-28 xl:max-h-[calc(100vh-8rem)] xl:overflow-y-auto xl:pr-1">
                  <Card><CardHeader className="pb-3"><CardTitle className="flex items-center gap-2 text-base">{validation?.publishable ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <AlertTriangle className="h-4 w-4 text-amber-600" />}발행 검사</CardTitle><CardDescription>오류 {validation?.errorCount ?? 0} · 경고 {validation?.warningCount ?? 0}</CardDescription></CardHeader>
                    <CardContent className="grid max-h-80 gap-2 overflow-y-auto">{issues.length === 0 ? <p className="text-sm text-emerald-700">결정적 검사를 통과했습니다.</p> : issues.map((issue, index) => <div key={`${issue.code}-${index}`} className="rounded-md border p-2 text-xs"><p className="font-semibold">{issue.message}</p><p className="mt-1 break-all text-muted-foreground">{issue.path}</p></div>)}</CardContent>
                  </Card>
                  {plugin.renderPreview(document)}
                  {history && <Card><CardHeader className="pb-3"><CardTitle className="flex items-center gap-2 text-base"><History className="h-4 w-4" />버전과 감사 이력</CardTitle><CardDescription>현재 v{history.currentVersionNumber ?? '-'}</CardDescription></CardHeader><CardContent className="grid max-h-80 gap-2 overflow-y-auto">
                    {history.versions.map((version) => <div key={version.versionNumber} className="rounded-md border p-2 text-xs"><div className="flex items-center justify-between gap-2"><span className="font-semibold">v{version.versionNumber}</span><Button size="sm" variant="ghost" disabled={working || history.currentVersionNumber === version.versionNumber} onClick={() => { if (!draft) return; const confirmation = window.prompt(`v${version.versionNumber}에서 복구 초안을 만들려면 ${draft.key.slug} 를 입력하세요.`); if (confirmation !== null) void runDraftAction(() => restoreContentVersion<TDocument>(plugin.type, draft.key.slug, draft.revision, version.versionNumber, confirmation)); }}><RotateCcw className="mr-1 h-3 w-3" />복구</Button></div><p className="mt-1 truncate text-muted-foreground">{version.sourceContentHash ?? 'legacy hash 없음'}</p></div>)}
                    {history.auditEntries.map((entry) => <div key={entry.auditId} className="border-t pt-2 text-xs"><p className="font-medium">{entry.action}</p><p className="text-muted-foreground">{entry.actorId} · {new Date(entry.occurredAt).toLocaleString()}</p></div>)}
                  </CardContent></Card>}
                  <Button variant="destructive" size="sm" disabled={!draft || working} onClick={() => { if (!draft) return; const confirmation = window.prompt(`폐기하려면 ${draft.key.slug} 를 입력하세요.`); if (confirmation === null) return; setWorking(true); void retireContent(plugin.type, draft.key.slug, confirmation).then(() => Promise.all([loadCatalog(), loadHistory(draft.key.slug)])).catch((failure) => setError(errorMessage(failure))).finally(() => setWorking(false)); }}>콘텐츠 폐기</Button>
                </aside>
              </div>
            </div>
          )}
        </main>
      </div>
      {working && <div className="fixed bottom-5 right-5 flex items-center gap-2 rounded-full border bg-background px-4 py-2 text-sm shadow"><Loader2 className="h-4 w-4 animate-spin" />처리 중</div>}
    </div>
  );
}
